/**
 * Pure helpers for the first-entry "copy the last populated document?" prompt.
 *
 * Extracted from MedStudentClinicPage.js so the two decisions that matter —
 * "should this visit be offered the copy at all?" and "has anyone actually
 * written in this note yet?" — can be unit-tested without mounting the page.
 *
 * See docs/superpowers/specs/2026-08-03-first-entry-copy-prior-doc-design.md
 */

// Mirrors FOLLOW_UP_TYPES in hooks/medstudent/usePatientDocument.js. These
// types already seed themselves from the most recent prior visit silently, so
// they must never also be prompted.
export const FOLLOW_UP_TYPES = new Set(['FFP', 'FFS']);

/**
 * localStorage key marking that a visit has consumed its one prompt.
 *
 * Deliberately NOT the legacy `medstudent-accumulate-prompted:` key. The old
 * accumulate effect wrote its flag on every completed run — including runs
 * that decided not to prompt — so reusing the key would permanently silence
 * visits whose prompt the user never actually saw.
 */
export function firstEntryPromptKey(uuid) {
  return `medstudent-first-entry-prompted:${uuid}`;
}

/**
 * Resolve `promise`, or `fallback` if it hasn't settled within `ms`.
 *
 * Guards the first-entry flow against a dependency that never settles. This is
 * not hypothetical: resolving the seed template reaches
 * `supabase.auth.getSession()`, and this app runs multiple GoTrueClient
 * instances against one storage key ("Multiple GoTrueClient instances detected"
 * in the console), which can deadlock on the shared auth lock. A hung await
 * there used to stall the prompt permanently.
 */
export function withTimeout(promise, ms, fallback = null) {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((resolve) => { timer = setTimeout(() => resolve(fallback), ms); }),
  ]).finally(() => clearTimeout(timer));
}

/** Whitespace-stripped plain text of a Quill delta's ops. Embeds are skipped. */
export function deltaPlainText(ops) {
  if (!Array.isArray(ops)) return '';
  return ops
    .map((op) => (typeof op?.insert === 'string' ? op.insert : ''))
    .join('')
    .replace(/\s+/g, '');
}

/**
 * The shared "this document has no real content" predicate — same shape used
 * by usePatientDocument.js and the Copy-Older candidate walk.
 */
export function isEmptyOps(ops) {
  if (!Array.isArray(ops) || ops.length === 0) return true;
  if (ops.length === 1 && (!ops[0]?.insert || ops[0].insert === '\n')) return true;
  return false;
}

/**
 * Gate: is this visit eligible for the first-entry prompt?
 *
 * Note this is the ONLY place the eligibility rules live. It does not look at
 * document contents — that is isUntouchedDoc's job, and it costs a network
 * round trip, so callers should check this first.
 */
export function priorDocPromptDecision({
  patient,
  rosterMode = false,
  patientReadOnly = false,
  isLoading = false,
  alreadyPrompted = false,
} = {}) {
  if (isLoading) return { offer: false, reason: 'visit still loading' };
  if (!patient) return { offer: false, reason: 'no patient row yet' };
  // Patient-level roster charts are dateless — there is no "previous event".
  if (rosterMode) return { offer: false, reason: 'roster chart (no prior event)' };
  // Read-only roles can't accept the copy, so don't offer it.
  if (patientReadOnly) return { offer: false, reason: 'read-only role' };
  if (alreadyPrompted) {
    return { offer: false, reason: 'already prompted for this visit (localStorage flag set)' };
  }
  if (!patient.date_) return { offer: false, reason: 'row has no date_' };

  const type = String(patient.type || '').toUpperCase().trim();
  if (FOLLOW_UP_TYPES.has(type)) {
    return { offer: false, reason: `follow-up type ${type} — seeds silently instead` };
  }

  // 'fresh' is an explicit per-patient opt-out set from the "Notes behavior
  // each session" radio in EditPatientModal. Unset means accumulate.
  const behavior = String(patient.notes_behavior || '').toLowerCase().trim();
  if (behavior === 'fresh') {
    return { offer: false, reason: "notes_behavior is 'fresh' — user opted out" };
  }

  return { offer: true, reason: 'eligible' };
}

/** Boolean form of priorDocPromptDecision. */
export function shouldOfferPriorDocCopy(args) {
  return priorDocPromptDecision(args).offer;
}

/**
 * Has anyone actually written in this visit's note yet?
 *
 * `parsedJson` is the visit's stored `.json` envelope, or null when the
 * download 404s / fails / cannot be parsed.
 *
 * Deliberately does NOT read the live editor. Under live collab the server
 * seeds every brand-new room with ~2,400 characters of template
 * (backend/api/collab/seed.py), so live text is never a valid emptiness
 * signal — that was the bug that kept the old prompt from ever firing.
 *
 * Under collab the `.json` signal is exact rather than heuristic:
 *   - DocumentBody only forwards text-change events with source === 'user'
 *     (DocumentBody.js:590), so hydration never triggers an autosave.
 *   - The collab server writes `.yjs` only, never `.json`
 *     (backend/api/collab/room.py:106-108, rationale at room.py:7-19).
 * So `.json` exists iff a client autosave ran, and that needs a real edit.
 *
 * `templateDocs` covers the collab-off path, where usePatientDocument uploads
 * the seeded template to `.json` itself. Candidates must be placeholder-FILLED
 * (i.e. resolveTemplateDoc output, not raw assets/templates JSON) or they can
 * never match a seeded document.
 */
export function isUntouchedDoc(parsedJson, templateDocs = []) {
  if (!parsedJson) return true;

  const ops = parsedJson?.delta?.ops;
  if (isEmptyOps(ops)) return true;

  const current = deltaPlainText(ops);
  if (!current) return true;

  return (templateDocs || []).some((tpl) => {
    const tplText = deltaPlainText(tpl?.delta?.ops);
    return tplText.length > 0 && tplText === current;
  });
}
